// addressSearch.js
import { Draw } from 'ol/interaction.js';
import VectorSource from 'ol/source/Vector.js';
import VectorLayer from 'ol/layer/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import * as config from './config.js';
import { fromCircle } from 'ol/geom/Polygon.js';
import { getMap } from './mapInstance.js';
import * as MapUtils from './mapUtils.js';
import Select from 'ol/interaction/Select.js';
import { secureFetch } from './securityUtil.js';
import { cancelActiveTools } from './mapUtils.js';
import { setActiveSidebarTool, showInfoMessage } from './dialogs.js';
import { countryConfig } from './config.js';
import { getSelectedCountryAbbreviation, getSelectedCountryCode, getSelectedLanguageCode } from './country-language.js';
import { getNextStyle, syncColorCycle } from './colorUtils.js';
import { addDrawEntry, resetDrawSummary, loadFromBackend, getDrawSummaryColors } from './drawSummaryPanel.js';
import { transform } from 'ol/proj.js';
import { canUseAddressAidsDraw } from './addressAidsAvailability.js';
import { startDrawAutoPan, stopDrawAutoPan } from './autoPan.js';
import { attachRadiusDrawLabel, createRadiusDrawStyle } from './radiusDrawStyle.js';

const ADDRESS_DRAW_AUTOPAN = {
  edgeThreshold: 64,
  panSpeedPx: 4
};


let colorIndex = 0;
let totalSelectedAddressCount = 0;

function propertiesToAddressRow(properties) {
  const row = {};

  if (Array.isArray(properties)) {
    properties.forEach(property => {
      const key = property?.key;
      if (key) row[key] = property.value;
    });
    return row;
  }

  if (properties && typeof properties === 'object') {
    Object.entries(properties).forEach(([key, value]) => {
      if (key) row[key] = value;
    });
  }

  return row;
}

function getSpatialResultCount(data) {
  const total = Number(data?.total);
  if (Number.isFinite(total)) return total;

  const totalFeatures = Number(data?.totalFeatures ?? data?.numberMatched ?? data?.numberReturned);
  if (Number.isFinite(totalFeatures)) return totalFeatures;

  return Array.isArray(data?.features) ? data.features.length : 0;
}

function getSpatialResultFeatures(data) {
  return Array.isArray(data?.features) ? data.features : [];
}

export let loadedColumns = [];

// Backend API base
const BACKEND_API = `${config.API_URL}spatial-search`; // adjust if needed

window.setCommand = function (text) {
  const cmd = document.getElementById('command-text');
  if (cmd) cmd.textContent = text;
};

window.setStatus = function (text) {
  const status = document.getElementById('status-text');
  if (status) status.textContent = text;
};

let draw; // global draw interaction
let addressAidsDrawActive = false;
let drawSource = new VectorSource();
let drawLayer = new VectorLayer({
  source: drawSource,
  zIndex: 9998,
  style: {
    'fill-color': 'rgba(255, 255, 255, 0.2)',
    'stroke-color': '#ffcc33',
    'stroke-width': 2
  }
});
drawLayer.set('selectedAreasKind', 'address');
drawLayer.set('selectedAreaLabel', 'Address Selection');
drawLayer.set('selectedAreaColor', '#ffcc33');
drawSource.on(['addfeature', 'removefeature', 'clear'], () => document.dispatchEvent(new CustomEvent('selected-areas-updated')));
drawLayer.on('change:visible', () => { document.dispatchEvent(new CustomEvent('selected-areas-updated')); if (!drawLayer.getVisible()) document.dispatchEvent(new CustomEvent('address-selection-layer-hidden')); });

let escapeCancelBound = false;

function removeActiveDrawInteraction(map) {
  if (!map || !draw) return;

  try {
    map.removeInteraction(draw);
  } catch (err) {
    console.warn('Failed to remove active draw interaction:', err);
  }

  draw = null;
}

function cancelAddressAidsDraw(reason = 'Cancelled') {
  addressAidsDrawActive = false;
  const map = getMap();

  if (draw && map) {
    try {
      draw.abortDrawing?.();
    } catch (err) {
      console.warn('Failed to abort draw interaction:', err);
    }
    removeActiveDrawInteraction(map);
  } else {
    draw = null;
  }

  stopDrawAutoPan();
  cancelActiveTools(reason);
}

function bindEscapeCancelOnce() {
  if (escapeCancelBound) return;

  document.addEventListener('keydown', evt => {
    if (evt.key !== 'Escape') return;

    const target = evt.target;
    const tagName = target?.tagName?.toLowerCase?.() || '';
    if (['input', 'textarea', 'select'].includes(tagName) || target?.isContentEditable) {
      return;
    }

    if (draw) {
      evt.preventDefault();
      cancelAddressAidsDraw('Cancelled');
    }
  });

  escapeCancelBound = true;
}

bindEscapeCancelOnce();

document.addEventListener('pc-selection-activating', () => {
  cancelAddressAidsDraw('Cancelled');
});

document.addEventListener('application-reset-active-tools', () => {
  cancelAddressAidsDraw('Ready');
});

document.addEventListener('address-aids-unavailable', evt => {
  if (!addressAidsDrawActive && !draw) return;
  cancelAddressAidsDraw(evt.detail?.reason || 'Zoom in to use address selection.');
});

function notifyAddressAidsActivating() {
  document.dispatchEvent(new CustomEvent('address-aids-activating'));
}


export function cancelDrawAndClear() {
  cancelAddressAidsDraw('Draw cancelled');
}

export function initDrawLayer() {
  const map = getMap();
  if (!map) return;

  const exists = map.getLayers().getArray().includes(drawLayer);
  if (!exists) {
    map.addLayer(drawLayer);
  }
    //FORCE it to top
    drawLayer.setZIndex(9998);
}


let lastQueryUrl = null;

export function handleDrawPolygon() {

  const map = getMap();
  if (!map) return;

  map.getInteractions().forEach(i => {
    if (i instanceof Draw) map.removeInteraction(i);
  });


  draw = new Draw({
    source: drawSource,
    type: 'Polygon'
  });
  draw.on('drawstart', () => startDrawAutoPan(map, ADDRESS_DRAW_AUTOPAN));
  draw.once('drawend', () => stopDrawAutoPan());
  map.addInteraction(draw);
}

export function handleDrawCircle() {

  const map = getMap();
  if (!map) return;


  draw = new Draw({
    source: drawSource,
    type: 'Circle'
  });
  draw.on('drawstart', () => startDrawAutoPan(map, ADDRESS_DRAW_AUTOPAN));
  draw.once('drawend', () => stopDrawAutoPan());
  map.addInteraction(draw);
}

// inside query() before building the URL
function buildCqlFilter(columnName, operator, value, dataType) {
  // Trim whitespace
  value = value?.toString().trim();

  // Detect if numeric
  const type = dataType?.toString().toLowerCase() || '';
  const isText = ['text', 'varchar', 'character varying', 'char', 'character'].includes(type);
  const isNumeric = !isText && !isNaN(value) && value !== '';

  let cqlValue;
  if (isNumeric) {
    // Numeric: no quotes
    cqlValue = value;
  } else {
    // String: must be quoted and escape single quotes
    const safeValue = value.replace(/'/g, "''");
    cqlValue = `'${safeValue}'`;
  }

  // Build condition based on operator
  switch (operator.toUpperCase()) {

    case 'ILIKE':
    case 'LIKE':
      return `${columnName} ${operator} '%${value}%'`;

    case '=':
    case '<>':
    case '>':
    case '<':
    case '>=':
    case '<=':
      return `${columnName} ${operator} ${cqlValue}`;

    default:
      console.warn(`Unsupported operator '${operator}', defaulting to '='`);
      return `${columnName} = ${cqlValue}`;
  }
}


export async function query() {

  const map = getMap();
  if (!map) return;

  const attribute = document.getElementById('column').value.trim();
  const operator = document.getElementById('operator').value.trim().toUpperCase();
  const value = document.getElementById('value').value.trim();
  const loadingBar = document.getElementById('loading-bar');
  if (loadingBar) loadingBar.style.display = 'block';

  const countryKey = getSelectedCountryAbbreviation()?.toUpperCase();
  const configCountry = countryConfig[countryKey];

    if (!configCountry) {
    setStatus('Invalid country selected');
    return;
  }

  const layerName = configCountry.layer;

  setCommand('Querying...!!!');
  setStatus('Please wait...');

  try {
    const selectedColumn = loadedColumns.find(c => c.columnName === attribute);
    const cqlFilter = buildCqlFilter(attribute, operator, value, selectedColumn?.dataType);

    lastQueryUrl = `${config.API_URL}feature-info/get-features` +
      `?layerName=${encodeURIComponent(layerName)}` +
      `&srsName=EPSG:3857` +
      `&cqlFilter=${encodeURIComponent(cqlFilter)}`;

    console.log('[AddressSearch] request URL:', lastQueryUrl);
    console.log('[AddressSearch] layerName:', layerName);
    console.log('[AddressSearch] cqlFilter:', cqlFilter);

    const response = await secureFetch(lastQueryUrl);
    const contentType = response.headers.get('Content-Type');
    if (!response.ok || !contentType.includes('application/json')) {
      const errorText = await response.text();
      console.error("Address Search error response:", errorText);
      setStatus('Address Search error. Check console.');
      return;
    }

    const data = await response.json();

    const format = new GeoJSON();
    const features = format.readFeatures(data, {
      dataProjection: 'EPSG:3857',
      featureProjection: map.getView().getProjection()
    });


    if (features.length === 0)
    {
        setStatus("0 addresses found");
        setCommand("No results");

        showInfoMessage("No addresses found for the given search criteria.", "Search Result" );
        return;
    }

    MapUtils.highlightFeaturesFromResult(data, true, 'EPSG:3857');

    if (features.length > 0) {
      const extent = MapUtils.getSelectionLayer().getSource().getExtent();
      map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 800 });
    }

    setStatus(`${features.length} addresses found`);
  } catch (err) {
    console.error('Query error:', err);
    setStatus('Query failed, check console.');
  } finally {
    if (loadingBar) loadingBar.style.display = 'none';
  }
}



export function showResultsTable() {
  const addresses = MapUtils.getSelectedAddresses_map();
  if (!addresses || !addresses.length) {
    console.warn("No selected addresses to display.");
    return;
  }

  // Remove old panel if exists
  let panel = document.getElementById("floating-table-panel");
  if (panel) panel.remove();

  // Create floating panel
  panel = document.createElement("div");
  panel.id = "floating-table-panel";
  panel.style.position = "absolute";
  panel.style.left = "0";
  panel.style.right = "0";
  panel.style.bottom = "0";
  panel.style.height = "250px";
  panel.style.background = "white";
  panel.style.borderTop = "2px solid #ccc";
  panel.style.boxShadow = "0 -4px 12px rgba(0,0,0,0.2)";
  panel.style.zIndex = "2000";
  panel.style.display = "flex";
  panel.style.flexDirection = "column";

  // --- Add header with close button and count ---
  const header = document.createElement("div");
  header.style.display = "flex";
  header.style.justifyContent = "space-between";
  header.style.alignItems = "center";
  header.style.padding = "4px 8px";
  header.style.borderBottom = "1px solid #ccc";
  header.style.background = "#f9f9f9";

  const countLabel = document.createElement("span");
  countLabel.textContent = `${addresses.length} feature(s) found`;
  header.appendChild(countLabel);

  const closeBtn = document.createElement("button");
  closeBtn.className = "btn-close btn-sm";
  closeBtn.type = "button";
  closeBtn.setAttribute("aria-label", "Close");
  closeBtn.title = "Close";
  closeBtn.onclick = () => panel.remove();
  header.appendChild(closeBtn);

  panel.appendChild(header);
  MapUtils.makeDraggable(panel, header);
  // --- end header ---

  // Scrollable container for the table
  const tableWrapper = document.createElement("div");
  tableWrapper.style.flex = "1";
  tableWrapper.style.overflowX = "auto";
  tableWrapper.style.overflowY = "auto";

  // Create table (same as before)...
  const table = document.createElement("table");
  table.setAttribute("class", "table table-bordered table-sm table-hover");
  table.style.minWidth = "100%";
  table.style.whiteSpace = "nowrap";

  // Extract columns
  const colNames = new Set();
  addresses.forEach(addr => {
    Object.keys(addr).forEach(k => colNames.add(k));
  });
  const columns = Array.from(colNames);

  // Header row
  const thead = document.createElement("thead");
  const trHead = document.createElement("tr");
  columns.forEach(c => {
    const th = document.createElement("th");
    th.textContent = c;
    trHead.appendChild(th);
  });
  thead.appendChild(trHead);
  table.appendChild(thead);

  // Body
  const tbody = document.createElement("tbody");
  addresses.forEach(addr => {
    const tr = document.createElement("tr");
    columns.forEach(c => {
      const td = document.createElement("td");
      td.textContent = addr[c] ?? "";
      tr.appendChild(td);
    });
    tbody.appendChild(tr);
  });
  table.appendChild(tbody);

  tableWrapper.appendChild(table);
  panel.appendChild(tableWrapper);
  document.getElementById("map").appendChild(panel);
}



export function drawShapeAndQuery(drawType) {
  const format = new GeoJSON();

  const map = getMap();
  if (!map) return;
  if (!canUseAddressAidsDraw(map)) return;

  notifyAddressAidsActivating();
  setCommand(`Draw ${drawType} to query`);
  setStatus('Waiting for geometry...');
  addressAidsDrawActive = true;

  const armDraw = () => {
    draw = new Draw({
      source: drawSource,
      type: drawType,
    });

    draw.on('drawstart', () => startDrawAutoPan(map, ADDRESS_DRAW_AUTOPAN));
    map.addInteraction(draw);

    draw.once('drawend', async function (event) {
        try {
        syncColorCycle(getDrawSummaryColors());
        event.feature.setStyle(getNextStyle());
        const feature = event.feature;
        const geojson = format.writeGeometryObject(feature.getGeometry(), {
          dataProjection: 'EPSG:4326',
          featureProjection: map.getView().getProjection()
        });

        const payload = JSON.stringify({ geometry: geojson });
        const endpoint = drawType === 'Circle' ? '/circle' : '/polygon';

        const response = await secureFetch(`${BACKEND_API}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload
        });

        const data = await response.json();
        console.assert(data.type === "FeatureCollection", "Unexpected data format");

        setStatus(`${data.features?.length || 0} feature(s) found`);
      } catch (err) {
        console.error('Draw query error:', err);
        setStatus('Error during draw query');
      } finally {
        stopDrawAutoPan();
        removeActiveDrawInteraction(map);
        if (addressAidsDrawActive) armDraw();
      }
    });
  };

  armDraw();

}

// Utility to strip CRS from geometries
function stripCRS(geojson) {
  const cleaned = JSON.parse(JSON.stringify(geojson));
  cleaned.features.forEach(f => {
    if (f.geometry && f.geometry.crs) {
      delete f.geometry.crs;
    }
  });
  return cleaned;
}

export function setupDrawInteraction(type) {

  const countryCode = getSelectedCountryCode();
  const languageCode = getSelectedLanguageCode();

    const map = getMap();
    if (!map) return;
    if (!canUseAddressAidsDraw(map)) return;

  notifyAddressAidsActivating();
  MapUtils.removeAllDrawInteractions();
  addressAidsDrawActive = true;

  const armDraw = () => {
    draw = new Draw({
      source: drawSource,
      type: type,
      ...(type === 'Circle' ? { style: createRadiusDrawStyle() } : {})
    });

    if (type === 'Circle') {
      attachRadiusDrawLabel(draw, map);
    }

    draw.on('drawstart', () => startDrawAutoPan(map, ADDRESS_DRAW_AUTOPAN));
    map.addInteraction(draw);

    draw.once('drawend', async function (event) {
        try {
        syncColorCycle(getDrawSummaryColors());
        const style = getNextStyle();
        event.feature.setStyle(style);

        const strokeColor = style.getStroke().getColor();
        const originalGeom = event.feature.getGeometry();
        let payload;

        if (originalGeom.getType() === 'Circle') {
          const polygon = fromCircle(originalGeom, 128);
          const geom4326 = polygon.clone().transform('EPSG:3857', 'EPSG:4326');
          payload = {
            type: "Polygon",
            geometry: new GeoJSON().writeGeometryObject(geom4326)
          };
        } else {
          const geom4326 = originalGeom.clone().transform('EPSG:3857', 'EPSG:4326');
          payload = {
            type: "Polygon",
            geometry: new GeoJSON().writeGeometryObject(geom4326)
          };
        }

        const endpoint = "/polygon";   // ALWAYS polygon

        const response = await secureFetch(`${config.API_URL}spatial-search${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...payload, countryCode, languageCode })
        });

        const data = await response.json();
        const count = getSpatialResultCount(data);

        addToSelectionCount(count);

        if (getSpatialResultFeatures(data).length) {
          const format = new GeoJSON();
          format.readFeatures(data, {
            dataProjection: 'EPSG:4326',
            featureProjection: map.getView().getProjection()
          });
        }

        const addressData = getSpatialResultFeatures(data).map(f => propertiesToAddressRow(f.properties));

        event.feature.set("addressData", addressData);
        addDrawEntry(strokeColor, count, event.feature);
      } finally {
        stopDrawAutoPan();
        removeActiveDrawInteraction(map);
        if (addressAidsDrawActive) armDraw();
      }
    });
  };

  armDraw();
}

export function cancelDrawOnly() {
  cancelAddressAidsDraw('Cancelled');
}


export function clearSelectionSet() {
  try {
    const map = getMap();
    if (!map) return;
    const messageEl = document.getElementById('message-text');
    const messageHtml = messageEl?.innerHTML;

    // Clear selection layer
    const selLayer = MapUtils.getSelectionLayer();
    if (selLayer) {
      selLayer.getSource().clear();
    }

    // Remove ALL draw interactions (IMPORTANT)
    MapUtils.removeAllDrawInteractions();

    // Clear drawn features
    drawSource.clear();

    // FIX: reset total count
    resetSelectionCount();

    // Force redraw
    map.render();

    // Reset panel
    resetDrawSummary();

    setStatus('Selection & drawings cleared.');
    setCommand('Ready');

    if (messageEl) {
      messageEl.innerHTML = messageHtml;
    }

  } catch (err) {
    console.error('Error clearing selection set:', err);
  }
}

export function clearAddressSearchResults() {
  try {
    MapUtils.clearSearchSelectionOnly();
    document.getElementById("floating-table-panel")?.remove();
    setStatus('Search results cleared.');
    setCommand('Ready');
  } catch (err) {
    console.error('Error clearing address search results:', err);
  }
}


export async function loadColumns() {
  try {
    const countryEl = document.getElementById("country-select");
    const languageEl = document.getElementById("language-select");

    if (!countryEl || !languageEl) {
      console.error("Country or Language element not found in DOM");
      return;
    }

    const countryId = countryEl.value;
    const languageId = languageEl.value;

    // Skip if either selector is empty
    if (!countryId || !languageId) {
      console.warn("Country or language not selected yet. Skipping columns load.");
      return;
    }

    const response = await secureFetch(
      `${config.API_URL}feature-info/address-search-columns?countryId=${countryId}&languageId=${languageId}`
    );

    if (!response.ok) throw new Error(`Failed to fetch columns: ${response.status}`);

    const columns = await response.json();
    loadedColumns = columns;
    populateColumns(columns);

  } catch (err) {
    console.error("Error loading columns:", err);
  }
}


function populateColumns(columns) {
  const columnSelect = document.getElementById("column");
  const operatorSelect = document.getElementById("operator");

  if (!columnSelect) {
    console.error("No #column element in DOM!");
    return;
  }

  // Fill the column select with backend-provided fields
  columnSelect.innerHTML = `<option value="">-- Select column --</option>`;
  columns.forEach(c => {
    const opt = document.createElement("option");
    opt.value = c.columnName;
    opt.textContent = c.displayLabel;
    columnSelect.appendChild(opt);
  });

  // When user selects a column, adjust operator list
  columnSelect.addEventListener("change", () => {
    const selectedCol = columns.find(c => c.columnName === columnSelect.value);
    operatorSelect.innerHTML = ""; // reset

    if (!selectedCol) return;

    const dt = selectedCol.dataType.toLowerCase();

    if (["text", "varchar"].includes(dt)) {
      operatorSelect.innerHTML = `
        <option value="=">Equal to</option>
        <option value="ILike">Like</option>
      `;
    } else if (["integer", "numeric", "double precision"].includes(dt)) {
      operatorSelect.innerHTML = `
        <option value="=">Equal to</option>
        <option value=">">Greater than</option>
        <option value="<">Less than</option>
      `;
    } else {
      operatorSelect.innerHTML = `<option value="=">Equal to</option>`;
    }
  });
}

// Run when DOM is ready
// Make query available in global scope if needed
// window.query = query;  <-- only if you insist on using inline HTML onclick
document.addEventListener("DOMContentLoaded", async () => {
  const searchForm = document.getElementById('search-form');
  const searchFormOpacity = document.getElementById('search-form-opacity');
  let searchFormOpacityLabelTimer = null;

  if (searchForm) searchForm.style.opacity = '1';
  searchFormOpacity?.addEventListener('input', event => {
    const value = Number(event.currentTarget.value);
    if (searchForm) searchForm.style.opacity = String(value / 100);
    const output = searchForm?.querySelector('output[for="search-form-opacity"]');
    if (!output) return;

    output.textContent = `${value}%`;
    output.style.left = `${7 + ((value - 30) / 70) * 40}px`;
    output.classList.add('is-visible');
    window.clearTimeout(searchFormOpacityLabelTimer);
    searchFormOpacityLabelTimer = window.setTimeout(
      () => output.classList.remove('is-visible'),
      2000
    );
  });

  const searchBtn = document.getElementById("search-button");
  if (searchBtn) {
    searchBtn.addEventListener("click", (e) => {
      e.preventDefault();
      query();
    });
  }

  const clearBtn = document.getElementById("clearSelection");

  if (clearBtn) {
    clearBtn.onclick = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();  // VERY IMPORTANT

      clearAddressSearchResults();
    };
  }

  const showBtn = document.getElementById("showResultsButton");
  if (showBtn) {
    showBtn.addEventListener("click", (e) => {
      e.preventDefault();
      showResultsTable();
    });
  }

  const loadBtn = document.getElementById("load-previous-results");
  if (loadBtn) {
    loadBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();

      const countryId = getSelectedCountryCode();
      const languageId = getSelectedLanguageCode();

      if (!countryId || !languageId) {
        alert("Please select country and language first");
        setActiveSidebarTool('.draw-panel-toggle');
        return;
      }

      try {
        await loadFromBackend({ openPanel: true });
      } finally {
        setActiveSidebarTool('.draw-panel-toggle');
      }
    });
  }

});

export function getDrawSource() {
  return drawSource;
}

export function removeFromSelectionCount(count) {
  totalSelectedAddressCount -= count;
  setStatus(`${totalSelectedAddressCount} addresses found`);
}

export function addToSelectionCount(count) {
  totalSelectedAddressCount += count;
  setStatus(`${totalSelectedAddressCount} addresses found`);
}

export function resetSelectionCount() {
  totalSelectedAddressCount = 0;
  setStatus(`${totalSelectedAddressCount} addresses found`);
}
